import { useState, useEffect, useRef } from 'react'

/**
 * Self-contained timer display component.
 * Manages its own interval and state — only this component re-renders every second.
 * Reads start time from localStorage (shared with useTaskTimer hook).
 * Hides itself when timer_done flag is set by the hook.
 * 
 * Usage:
 *   <TaskTimerDisplay duration={600} sessionId={663} />
 */
export default function TaskTimerDisplay({ duration, sessionId }) {
  const [display, setDisplay] = useState('')
  const [hidden, setHidden] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    if (!sessionId) return

    const update = () => {
      // Check if task is done
      if (localStorage.getItem(`timer_done_${sessionId}`)) {
        setHidden(true)
        clearInterval(intervalRef.current)
        return
      }

      const savedStart = localStorage.getItem(`timer_start_${sessionId}`)
      if (!savedStart) {
        setDisplay('')
        return
      }

      const elapsed = Math.floor((Date.now() - parseInt(savedStart, 10)) / 1000)
      const remaining = Math.max(duration - elapsed, 0)
      const minutes = Math.floor(remaining / 60)
      const seconds = remaining % 60
      setDisplay(`${minutes}:${seconds.toString().padStart(2, '0')}`)

      if (remaining <= 0) {
        clearInterval(intervalRef.current)
      }
    }

    update()
    intervalRef.current = setInterval(update, 1000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [sessionId, duration])

  if (hidden || !display) return null

  return (
    <div style={{
      fontSize: '13px',
      fontWeight: 500,
      color: '#e5e7eb',
      fontFamily: 'Monaco, Menlo, monospace',
      background: 'rgba(255, 255, 255, 0.1)',
      padding: '4px 10px',
      borderRadius: '6px',
    }}>
      ⏱ {display}
    </div>
  )
}